// #region Imports
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { StoredAttachment } from '../../utils/fileUtils';
// #endregion

// #region Types
export interface FeedbackData {
  appName?: string;
  appVersion?: string;
  userAgent?: string;
  email?: string;
  username?: string;
  date?: string;
  type?: 'feedback' | 'bug' | null;
  // Feedback wizard
  featuresNote?: number;
  featuresComment?: string;
  ergonomicsNote?: number;
  ergonomicsComment?: string;
  bugsNote?: number;
  bugsComment?: string;
  globalComment?: string;
  // Bug report
  bugComment?: string;
  /** Base64-encoded attachments */
  attachments?: StoredAttachment[];
}

export type FeedbackStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface FeedbackState {
  lastFeedbackDate: string | null;
  open: boolean | 'manually';
  status: FeedbackStatus;
  error: string | null;
}

export interface FeedbackRootState {
  feedback: FeedbackState;
}
// #endregion

// #region Slice
const initialState: FeedbackState = {
  lastFeedbackDate: null,
  open: false,
  status: 'idle',
  error: null,
};

export const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    setLastFeedbackDate(state, action: PayloadAction<string | null>) {
      state.lastFeedbackDate = action.payload;
    },
    setFeedBackOpen(state, action: PayloadAction<boolean | 'manually'>) {
      state.open = action.payload;
    },
    resetFeedbackStatus(state) {
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(sendFeedback.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(sendFeedback.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(sendFeedback.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Unknown error';
      });
  },
});
// #endregion

// #region Async thunk
/**
 * Sends feedback via a consumer-provided API function.
 * The `apiFn` parameter should be stored in middleware or passed via a Redux middleware setup.
 * For simpler use, consumers can dispatch this thunk with their own fetch logic.
 */
export const sendFeedback = createAsyncThunk(
  'feedback/sendFeedback',
  async ({ data, apiFn }: { data: FeedbackData; apiFn: (data: FeedbackData) => Promise<void> }) => {
    await apiFn(data);
  },
);
// #endregion

// #region Actions
export const { setLastFeedbackDate, setFeedBackOpen, resetFeedbackStatus } = feedbackSlice.actions;
// #endregion

// #region Selectors
export const selectIsFeedbackOpen = (state: FeedbackRootState) => state.feedback.open;

export const selectIsFeedbackExpired = (state: FeedbackRootState): boolean => {
  const lastFeedbackDate = state.feedback.lastFeedbackDate
    ? new Date(state.feedback.lastFeedbackDate)
    : null;

  if (!lastFeedbackDate) return true;

  const currentDate = new Date();
  const currentYearMonth = currentDate.getFullYear() * 100 + currentDate.getMonth();
  const lastYearMonth = lastFeedbackDate.getFullYear() * 100 + lastFeedbackDate.getMonth();
  return currentYearMonth - lastYearMonth >= 1;
};

export const selectFeedbackStatus = (state: FeedbackRootState) => state.feedback.status;
// #endregion

export default feedbackSlice.reducer;
